/**
 * Firebase Cloud Messaging Service
 * Handles push notification registration and token management
 */

import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging'
import app from '../config/firebase'

// Only initialize messaging if supported (HTTPS required)
let messaging = null
try {
  if (typeof window !== 'undefined' && 'serviceWorker' in navigator && window.location.protocol === 'https:') {
    messaging = getMessaging(app)
  }
} catch (error) {
  console.warn('Firebase Messaging not available:', error.message)
}

/**
 * Request notification permission and get FCM token
 * @returns {Promise<string|null>} FCM token or null if permission denied
 */
export const requestNotificationPermission = async () => {
  try {
    // Check if running on HTTP (not HTTPS)
    if (window.location.protocol !== 'https:') {
      console.warn('Push notifications require HTTPS. Skipping FCM initialization.')
      return null
    }

    // Check if notifications are supported
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications')
      return null
    }

    // Check if service workers are supported
    if (!('serviceWorker' in navigator)) {
      console.warn('Service workers are not supported')
      return null
    }

    // Check if messaging is initialized
    if (!messaging) {
      console.warn('Firebase Messaging not initialized')
      return null
    }

    // Request permission
    const permission = await Notification.requestPermission()
    
    if (permission === 'granted') {
      console.log('Notification permission granted')

      // Register service worker
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js')
      console.log('Service Worker registered:', registration)

      // Get FCM token
      const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY
      
      if (!vapidKey || vapidKey === 'your_vapid_key_here') {
        console.warn('VAPID key not configured. Please add it to .env')
        return null
      }

      const token = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: registration
      })

      if (token) {
        console.log('FCM Token:', token)
        return token
      } else {
        console.log('No registration token available')
        return null
      }
    } else if (permission === 'denied') {
      console.warn('Notification permission denied')
      return null
    } else {
      console.log('Notification permission dismissed')
      return null
    }
  } catch (error) {
    console.error('Error getting FCM token:', error)
    return null
  }
}

/**
 * Listen for foreground messages
 * @param {Function} callback - Function to handle incoming messages
 */
export const onForegroundMessage = (callback) => {
  if (!messaging) {
    console.warn('Firebase Messaging not available')
    return () => {} // Return no-op unsubscribe function
  }
  
  return onMessage(messaging, (payload) => {
    console.log('Foreground message received:', payload)
    callback(payload)
  })
}

/**
 * Show browser notification
 * @param {string} title - Notification title
 * @param {Object} options - Notification options
 */
export const showNotification = (title, options = {}) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    const notification = new Notification(title, {
      icon: options.icon || '/favicon.png',
      badge: '/favicon.png',
      body: options.body || '',
      data: options.data || {},
      tag: options.tag || 'default',
      requireInteraction: options.requireInteraction || false,
      ...options
    })

    // Handle notification click
    notification.onclick = (event) => {
      event.preventDefault()
      window.focus()
      if (options.url) {
        window.location.href = options.url
      }
      notification.close()
    }

    return notification
  }
  return null
}

/**
 * Check current notification permission status
 * @returns {string} 'granted', 'denied', or 'default'
 */
export const getNotificationPermission = () => {
  if ('Notification' in window) {
    return Notification.permission
  }
  return 'unsupported'
}
